import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Info, Radio, Clock3, History, Ban, TrendingUp, TrendingDown } from "lucide-react";
import { useLang, WA_LINK } from "../i18n";
import { Chapter, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORDER = ["copper", "zinc", "nickel", "lead", "aluminum", "tin", "cobalt", "antimony", "gold", "silver", "platinum", "palladium"];

const STATUS_STYLE = {
  live: "text-[#4ADE80] border-[#4ADE80]/30 bg-[#4ADE80]/10",
  delayed: "text-[#C9A227] border-[#C9A227]/30 bg-[#C9A227]/10",
  latest: "text-[#EDF1F6] border-[#EDF1F6]/30 bg-[#EDF1F6]/10",
  unavailable: "text-[#EDF1F6]/50 border-[#EDF1F6]/15 bg-transparent",
};
const STATUS_ICON = { live: Radio, delayed: Clock3, latest: History, unavailable: Ban };

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "es" ? "es-ES" : "en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function Sparkline({ points, w = 112, h = 30 }) {
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1 || 1);
  const coords = points.map((p, i) => [i * step, h - 3 - ((p - min) / span) * (h - 6)]);
  const line = coords.map((c) => c.map((n) => n.toFixed(1)).join(",")).join(" ");
  const up = points[points.length - 1] >= points[0];
  const stroke = up ? "#4ADE80" : "#F87171";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block" aria-hidden="true">
      <defs>
        <linearGradient id={`sg-${up}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.25" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${line} ${w},${h}`} fill={`url(#sg-${up})`} />
      <polyline points={line} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="2" fill={stroke} />
    </svg>
  );
}

export function MarketPrices() {
  const { t, lang } = useLang();
  const [metals, setMetals] = useState(null);
  const [history, setHistory] = useState({});

  useEffect(() => {
    let alive = true;
    const loadHistory = (id) => {
      setHistory((h) => (id in h ? h : { ...h, [id]: null }));
      axios
        .get(`${API}/history`, { params: { id } })
        .then((r) => alive && setHistory((h) => ({ ...h, [id]: r.data.points })))
        .catch(() => alive && setHistory((h) => ({ ...h, [id]: [] })));
    };
    const load = () =>
      axios
        .get(`${API}/prices`)
        .then((r) => {
          if (!alive) return;
          setMetals(r.data.metals);
          r.data.metals.filter((m) => m.price != null).forEach((m) => loadHistory(m.id));
        })
        .catch(() => alive && setMetals((prev) => (prev === null ? [] : prev)));
    load();
    const interval = setInterval(load, 120000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, []);

  const byId = Object.fromEntries((metals || []).map((m) => [m.id, m]));
  const rows = metals === null ? null : ORDER.map((id) => byId[id] || { id, price: null, unit: id === "antimony" ? "CNY/MT" : id.match(/gold|silver|platinum|palladium/) ? "USD/troy oz" : "USD/MT", source: "—", updated: null, status: "unavailable" });

  return (
    <section id="prices" data-testid="prices-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Chapter kicker={t.prices.kicker} title={t.prices.title} subtitle={t.prices.subtitle} testId="prices" />

        <Reveal>
          <div className="glass overflow-hidden" style={{ borderRadius: "4px 24px 4px 24px" }} data-testid="prices-table">
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#EDF1F6]/75 border-b border-[#C9A227]/15">
                    <th className="px-5 py-3.5 font-medium">{t.prices.material}</th>
                    <th className="px-5 py-3.5 font-medium text-right">{t.prices.price}</th>
                    <th className="px-5 py-3.5 font-medium">{t.prices.trend30}</th>
                    <th className="px-5 py-3.5 font-medium">{t.prices.unit}</th>
                    <th className="px-5 py-3.5 font-medium">{t.prices.source}</th>
                    <th className="px-5 py-3.5 font-medium">{t.prices.updated}</th>
                    <th className="px-5 py-3.5 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows === null &&
                    ORDER.map((id) => (
                      <tr key={id} className="border-t border-white/5 animate-pulse">
                        <td className="px-5 py-3.5 text-[#F5F7FA]">{t.prices.names[id]}</td>
                        <td colSpan={6} className="px-5 py-3.5 text-[#EDF1F6]/60 italic">{t.prices.loading}</td>
                      </tr>
                    ))}
                  {rows !== null &&
                    rows.map((m) => {
                      const Icon = STATUS_ICON[m.status] || Ban;
                      const label = m.status === "live" ? t.prices.live : m.status === "delayed" ? t.prices.delayed : m.status === "latest" ? t.prices.latest : t.prices.unavailableStatus;
                      const pts = history[m.id];
                      return (
                        <tr key={m.id} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors duration-300" data-testid={`price-row-${m.id}`}>
                          <td className="px-5 py-3.5 font-medium text-[#F5F7FA]">{t.prices.names[m.id]}</td>
                          <td className="px-5 py-3.5 text-right font-semibold tabular-nums text-[#C9A227]" data-testid={`price-value-${m.id}`}>
                            {m.price != null ? `${(m.unit || "").startsWith("CNY") ? "¥" : "$"}${m.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                            {m.changePct != null && (
                              <span
                                className={`ml-2 inline-flex items-center gap-0.5 align-middle text-xs font-semibold ${m.changePct >= 0 ? "text-[#4ADE80]" : "text-[#F87171]"}`}
                                data-testid={`price-change-${m.id}`}
                              >
                                {m.changePct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                                {m.changePct >= 0 ? "+" : ""}{m.changePct}%
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5" data-testid={`price-trend-${m.id}`}>
                            {m.price == null ? (
                              <span className="text-[#EDF1F6]/50">—</span>
                            ) : pts == null ? (
                              <div className="h-[30px] w-28 animate-pulse rounded bg-white/5" />
                            ) : pts.length > 1 ? (
                              <Sparkline points={pts} />
                            ) : (
                              <span className="text-[#EDF1F6]/50">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-[#EDF1F6]">{m.unit}</td>
                          <td className="px-5 py-3.5 text-[#EDF1F6]">{m.source}</td>
                          <td className="px-5 py-3.5 text-[#EDF1F6]/82 whitespace-nowrap">{formatDate(m.updated, lang)}</td>
                          <td className="px-5 py-3.5 text-right">
                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLE[m.status] || STATUS_STYLE.unavailable}`} data-testid={`price-status-${m.id}`}>
                              <Icon size={11} /> {label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  {rows !== null && rows.some((m) => m.status === "unavailable") && (
                    <tr className="border-t border-[#C9A227]/15 bg-[#C9A227]/5">
                      <td colSpan={7} className="px-5 py-3 text-xs italic text-[#EDF1F6]" data-testid="prices-unavailable-note">
                        {t.prices.unavailable} — {rows.filter((m) => m.status === "unavailable").map((m) => t.prices.names[m.id]).join(", ") || "—"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-[#C9A227]/15 px-5 py-4">
              <p className="text-xs text-[#EDF1F6]/75">{t.prices.refreshNote}</p>
              <p className="text-xs text-[#EDF1F6]/75">
                {t.prices.officialSources}{" "}
                <a data-testid="lme-official-link" href="https://www.lme.com/en/Metals" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C9A227] hover:text-[#F5F7FA] transition-colors duration-300 font-medium">
                  {t.prices.lmeLink} <ExternalLink size={12} />
                </a>
                {" · "}
                <a data-testid="lbma-official-link" href="https://www.lbma.org.uk/prices-and-data" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C9A227] hover:text-[#F5F7FA] transition-colors duration-300 font-medium">
                  {t.prices.lbmaLink} <ExternalLink size={12} />
                </a>
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-6">
          <div className="glass overflow-hidden" style={{ borderRadius: "4px 24px 4px 24px" }} data-testid="other-materials-table">
            <div className="border-b border-[#C9A227]/15 px-6 py-4">
              <h3 className="font-display text-base md:text-lg tracking-wider text-[#F5F7FA]">{t.prices.otherTitle}</h3>
            </div>
            <ul>
              {t.prices.other.map((name) => (
                <li key={name} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 border-t border-white/5 px-6 py-3.5 text-sm">
                  <span className="text-[#F5F7FA]">{name}</span>
                  <a
                    data-testid={`commercial-price-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#C9A227] hover:text-[#F5F7FA] transition-colors duration-300"
                  >
                    {t.prices.commercial}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-8">
          <div className="flex items-start gap-4 border border-[#C9A227]/25 bg-[#151B24]/60 backdrop-blur-md p-6" style={{ borderRadius: "4px 20px 4px 20px" }} data-testid="prices-disclaimer">
            <Info size={20} className="mt-0.5 shrink-0 text-[#C9A227]" />
            <p className="text-sm text-[#EDF1F6] leading-relaxed italic">{t.prices.disclaimer}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
